import { Box, IconButton, Spacer, Stack, Text } from '@chakra-ui/react'
import { faChevronLeft, faUserGroup } from '@fortawesome/pro-regular-svg-icons'
import { faArrowUpRightFromSquare, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import type { ConversationHeaderConversationFragment } from '@/client/services/hasura/generated/graphql'
import { getConversationUrl } from '@/client/services/url'

import ShwLink from '@/components/ShwLink'
import Icon from '@/components/UI/Icon/Icon'
import NoiseTextureOverlay from '@/components/UI/NoiseTextureOverlay'
import IconButtonWithNumber from '@/components/Worlds/UI/Button/IconButtonWithNumber'

import ConversationAvatars from './ConversationAvatars'
import ConversationMembersModal from './ConversationMembersModal'
import ConversationMenu from './ConversationMenu'
import ConversationTitle from './ConversationTitle'
import { LIST_CONVERSATION } from './constants'

function ConversationHeader({
  conversation,
  isAdminView = false,
  variant = 'default',
  onMinimize,
  onBack,
  hideBackButton = false,
}: {
  conversation: ConversationHeaderConversationFragment
  isAdminView?: boolean
  variant?: 'default' | 'floating'
  onMinimize?: () => void
  onBack?: () => void
  hideBackButton?: boolean
}): React.ReactElement {
  const [showMemberModal, setShowMemberModal] = useState(false)

  const fgColor = variant === 'floating' ? 'purple.contrast' : 'fg'

  const iconButton = (
    <IconButton
      variant="icon"
      icon={<Icon faIcon={faChevronLeft} size="md" />}
      aria-label="Back to messages"
      cursor="pointer"
      onClick={onBack}
      color={fgColor}
      _hover={variant === 'floating' ? 'none' : undefined}
    />
  )

  const backButton = hideBackButton ? null : variant === 'floating' ? (
    iconButton
  ) : (
    <ShwLink
      href={`#${LIST_CONVERSATION}`}
      isExternal={false}
      display={{ base: 'flex', md: 'none' }}
      mr={{ base: '16px', md: '0' }}
    >
      {iconButton}
    </ShwLink>
  )

  return (
    <Box
      bgGradient={
        variant === 'floating'
          ? `linear(to left, ${chakraTokenToVar('purple.solid')}, ${chakraTokenToVar('cyan.solid')})`
          : 'none'
      }
      color={fgColor}
      position="relative"
    >
      <NoiseTextureOverlay width="100%" height="100%" />
      <Stack
        borderBottom={`2px solid ${chakraTokenToVar('border.muted')}`}
        p="16px"
        direction="row"
        align="center"
        width="100%"
        minHeight="70px"
      >
        {backButton}
        <Box mr="16px">
          <ConversationAvatars conversation={conversation} size="sm" />
        </Box>
        <ConversationTitle conversation={conversation} includeLinkToEntity />
        <Spacer />
        {variant === 'default' ? (
          <>
            <ConversationMembersModal
              open={showMemberModal}
              onClose={() => setShowMemberModal(false)}
              conversation={conversation}
            />
            <IconButtonWithNumber
              icon={faUserGroup}
              label={conversation.conversation_users.length}
              onClick={() => setShowMemberModal(true)}
              variant="subtle"
            />
            <ConversationMenu conversation={conversation} showAdminOptions={isAdminView} />
          </>
        ) : (
          <>
            <ShwLink href={getConversationUrl(conversation.external_uuid)}>
              <IconButton
                variant="icon"
                icon={<Icon faIcon={faArrowUpRightFromSquare} size="md" />}
                aria-label="Open conversation"
                color={fgColor}
                _hover={variant === 'floating' ? 'none' : undefined}
              />
            </ShwLink>
            <IconButton
              variant="icon"
              icon={<Icon faIcon={faXmark} size="lg" />}
              aria-label="Minimize conversation"
              cursor="pointer"
              onClick={onMinimize}
              color={fgColor}
              _hover={variant === 'floating' ? 'none' : undefined}
            />
          </>
        )}
      </Stack>
      {variant === 'default' ? (
        <Box py="12px" px="24px" borderBottom="2px solid" borderColor="border.muted">
          <Text
            color="fg.subtle"
            fontSize="14px"
            textAlign="center"
            display={{ base: 'none', md: 'block' }}
          >
            Messages are monitored by our volunteer safety team. Please do not share any personal
            information including full name, email, location, and social media handles!
          </Text>
          <Text
            color="fg.subtle"
            fontSize="14px"
            textAlign="center"
            display={{ base: 'block', md: 'none' }}
          >
            Messages are monitored by our volunteer safety team. Please do not share any personal
            information!
          </Text>
        </Box>
      ) : null}
    </Box>
  )
}

export default ConversationHeader
